// src/pages/TicketDetail.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/useToast';
import CommentSection from '../../components/Tickets/CommentSection';
import TicketScoreModal from '../../components/Tickets/TicketScoreModal';
import QuotationCard from '../../components/Tickets/QuotationCard';
import { 
  ArrowLeft, Clock, User, XCircle, RefreshCw, 
  Image, Download, ExternalLink, TrendingUp
} from 'lucide-react';
import { buildApiUrl, buildUploadUrl, UPLOADS_URL } from '../../config/api';

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuthStore();
  const { success, error: showError } = useToast();

  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showScoreModal, setShowScoreModal] = useState(false);
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [quotation, setQuotation] = useState<any>(null);

  useEffect(() => {
    loadTicket();
  }, [id, refreshKey]);

const loadTicket = async () => {
  try {
    setLoading(true);
    console.log('[TICKET] Cargando ticket:', id);

    const response = await fetch(buildApiUrl(`/tickets/${id}`), {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}`);
    }

    const data = await response.json();
    console.log('[TICKET] Ticket cargado:', data.ticket);
    setTicket(data.ticket);

    // Cargar cotizacion si existe
    if (data.ticket.requires_quotation === 1) {
      const quotationResponse = await fetch(buildApiUrl(`/tickets/${id}/quote`), {
        credentials: 'include'
      });
      
      if (quotationResponse.ok) {
        const quotationData = await quotationResponse.json();
        setQuotation(quotationData.quotation);
        console.log('[TICKET] Cotizacion cargada:', quotationData.quotation);
      }
    }

  } catch (err) {
    console.error('[TICKET] Error cargando ticket:', err);
    showError('Error al cargar el ticket');
  } finally {
    setLoading(false);
  }
};
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
        <div className="max-w-6xl mx-auto text-center">
          <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            Ticket no encontrado
          </h2>
          <button
            onClick={() => navigate('/dashboard/tickets')}
            className="mt-4 text-blue-500 hover:text-blue-600"
          >
            Volver a tickets
          </button>
        </div>
      </div>
    );
  }

 
 const handleStatusChange = async () => {
  if (!selectedStatus) return;
  
  setIsUpdating(true);
  try {
    const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
    const response = await fetch(buildApiUrl(`/tickets/${ticket.id}/status`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({
        status: selectedStatus,
        notes: statusNotes
      })
    });

    if (!response.ok) {
      throw new Error('Error al cambiar estado');
    }

    success('Estado actualizado correctamente');
    setShowStatusModal(false);
    setSelectedStatus('');
    setStatusNotes('');
    loadTicket(); // Recargar ticket
  } catch (err) {
    console.error('[STATUS] Error:', err);
    showError('Error al cambiar el estado');
  } finally {
    setIsUpdating(false);
  }
};

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-500';
      case 'high': return 'bg-orange-500/20 text-orange-500';
      case 'medium': return 'bg-yellow-500/20 text-yellow-500';
      case 'low': return 'bg-green-500/20 text-green-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500/20 text-green-500';
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'in_progress': return 'bg-blue-500/20 text-blue-500';
      case 'closed': return 'bg-gray-500/20 text-gray-500';
      case 'resolved': return 'bg-purple-500/20 text-purple-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard/tickets')}
            className={`flex items-center gap-2 ${
              isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            } transition-colors group`}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Volver a tickets
          </button>


        </div>

        {/* Ticket Info Card */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        } p-6`}>
          {/* Title and Status */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`}>
                {ticket.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
                  {ticket.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                }`}>
                  {ticket.category}
                </span>
              </div>
            </div>

            <div className="text-right">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Ticket ID
              </p>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {ticket.id}
              </p>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                Usuario
              </p>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-500" />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {ticket.user_name}
                </p>
              </div>
            </div>

            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                Creado
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(ticket.created_at).toLocaleDateString('es-CL')}
                </p>
              </div>
            </div>

            {ticket.motor && (
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Motor
                </p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {ticket.motor}
                </p>
              </div>
            )}

            {ticket.component && (
              <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Componente
                </p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {ticket.component}
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`}>
              Descripcion
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap leading-relaxed`}>
              {ticket.description}
            </p>
          </div>

          {/* Technical Info */}
          {(ticket.motor || ticket.configuration || ticket.component || ticket.part_number) && (
            <div className={`mt-6 p-4 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} rounded-lg`}>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`}>
                Informacion Tecnica
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {ticket.motor && (
                  <div>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Motor:</span>
                    <span className={`ml-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.motor}
                    </span>
                  </div>
                )}
                {ticket.configuration && (
                  <div>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Configuracion:</span>
                    <span className={`ml-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.configuration}
                    </span>
                  </div>
                )}
                {ticket.component && (
                  <div>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Componente:</span>
                    <span className={`ml-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.component}
                    </span>
                  </div>
                )}
                {ticket.part_number && (
                  <div>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>N Parte:</span>
                    <span className={`ml-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.part_number}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

       {/* Imagenes Adjuntas */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-6`}>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <Image className="w-5 h-5" />
              Archivos Adjuntos ({ticket.attachments.length})
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {ticket.attachments.map((attachment: any, index: number) => {
                // URL ABSOLUTA GARANTIZADA usando buildUploadUrl
                const fileUrl = buildUploadUrl('tickets', attachment.filename);
                
                console.log('[ATTACHMENT] URL construida:', fileUrl);
                console.log('[ATTACHMENT] Attachment data:', {
                  filename: attachment.filename,
                  original_name: attachment.original_name,
                  mime_type: attachment.mime_type
                });
                
                return (
                  <div
                    key={attachment.id || index}
                    className={`${
                      isDark ? 'bg-gray-700 border-gray-600 hover:border-blue-500' : 'bg-gray-50 border-gray-300 hover:border-blue-400'
                    } border rounded-lg overflow-hidden transition-all cursor-pointer group`}
                  >
                    {/* Preview de imagen */}
                    {attachment.mime_type?.startsWith('image/') ? (
                      <div className="relative aspect-square">
                        <img
                          src={fileUrl}
                          alt={attachment.original_name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onClick={() => window.open(fileUrl, '_blank')}
                          onError={(e) => {
                            console.error('[ATTACHMENT] Error cargando imagen:', fileUrl);
                            console.error('[ATTACHMENT] Error event:', e);
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.classList.add('bg-red-100');
                              parent.innerHTML = `<div class="flex items-center justify-center h-full"><span class="text-red-500 text-xs p-2">Error cargando imagen</span></div>`;
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ) : (
                      <div className={`aspect-square flex items-center justify-center ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}>
                        <Download className={`w-12 h-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      </div>
                    )}
                    
                    {/* Info del archivo */}
                    <div className="p-3">
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {attachment.original_name}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                        {((attachment.size || 0) / 1024).toFixed(1)} KB
                      </p>
                      
                      {/* Boton descargar */}
                      <a
                        href={fileUrl}
                        download={attachment.original_name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition-colors"
                        onClick={(e) => {
                          console.log('[ATTACHMENT] Descargando:', fileUrl);
                        }}
                      >
                        <Download className="w-4 h-4" />
                        Descargar
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      
      {/* Boton Evaluar - SOLO si NO tiene score y es admin/support/technician */}
          {!ticket.score && ['admin', 'support', 'technician', 'agent'].includes(user?.role || '') && (
            <div className={`${isDark ? 'bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'} border rounded-xl p-6`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2 flex items-center gap-2`}>
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    Evaluacion Pendiente
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Este ticket aun no ha sido evaluado. La evaluacion determinara si requiere una cotizacion formal antes de proceder.
                  </p>
                </div>
                <button
                  onClick={() => setShowScoreModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <TrendingUp className="w-5 h-5" />
                  Evaluar Ahora
                </button>
              </div>
            </div>
          )}
          
          {/* Boton Cambiar Estado (solo admin/support/technician) */}
              {['admin', 'support', 'technician', 'agent'].includes(user?.role || '') && ticket.status !== 'closed' && (
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl"
                >
                  <RefreshCw className="w-5 h-5" />
                  Cambiar Estado
                </button>
              )}
          
          {/* Resultado de Evaluacion - SOLO si ya tiene score */}
          {ticket.score && (
            <div className={`${
              ticket.requires_quotation === 1
                ? isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                : isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
            } border rounded-xl p-6`}>
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  ticket.requires_quotation === 1
                    ? 'bg-yellow-500 text-white'
                    : 'bg-green-500 text-white'
                }`}>
                  <span className="text-2xl font-bold">{parseFloat(ticket.score || 0).toFixed(0)}</span>
                </div>
                
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                    {ticket.requires_quotation === 1 ? 'Cotizacion Requerida' : 'Listo para Proceder'}
                  </h3>
                  
                  {ticket.requires_quotation === 1 ? (
                    <div className="space-y-2">
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <strong>Motivo:</strong> El score de <strong>{parseFloat(ticket.score).toFixed(2)}</strong> supera el umbral de cotizacion.
                        Este ticket requiere una cotizacion formal debido a su complejidad, tiempo estimado o valor del trabajo.
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Se ha generado automaticamente una cotizacion que debe ser aprobada por el cliente antes de proceder.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <strong>Motivo:</strong> El score de <strong>{parseFloat(ticket.score).toFixed(2)}</strong> esta por debajo del umbral.
                        Este trabajo puede realizarse sin cotizacion previa.
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        El equipo puede proceder directamente con la reparacion o servicio solicitado.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Cotizacion */}
          {quotation && (
            <QuotationCard
              quotation={quotation}
              onUpdate={() => loadTicket()}
            />
          )}

        {/* Seccion de Comentarios */}
        <CommentSection
          ticketId={ticket.id}
          comments={ticket.comments || []}
          onCommentAdded={() => setRefreshKey(prev => prev + 1)}
        />
         {/* Modal de Cambio de Estado */}
            {showStatusModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border w-full max-w-md`}>
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-700">
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                      <RefreshCw className="w-6 h-6" />
                      Cambiar Estado del Ticket
                    </h3>
                  </div>
            
                  {/* Body */}
                  <div className="p-6 space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Nuevo Estado
                      </label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      >
                        <option value="">Seleccionar estado...</option>
                        <option value="open">Abierto</option>
                        <option value="in_progress">En Progreso</option>
                        <option value="waiting_user">Esperando Usuario</option>
                        <option value="resolved">Resuelto</option>
                        <option value="closed">Cerrado</option>
                      </select>
                    </div>
            
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Notas (Opcional)
                      </label>
                      <textarea
                        value={statusNotes}
                        onChange={(e) => setStatusNotes(e.target.value)}
                        rows={3}
                        placeholder="Agregar comentarios sobre el cambio de estado..."
                        className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} resize-none`}
                      />
                    </div>
                  </div>
            
                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowStatusModal(false);
                        setSelectedStatus('');
                        setStatusNotes('');
                      }}
                      className={`px-6 py-3 rounded-lg font-medium ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}
                      disabled={isUpdating}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleStatusChange}
                      disabled={!selectedStatus || isUpdating}
                      className="px-6 py-3 rounded-lg font-medium bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? 'Actualizando...' : 'Cambiar Estado'}
                    </button>
                  </div>
                </div>
              </div>
            )}
        {/* Modal de Score */}
        {showScoreModal && (
          <TicketScoreModal
            ticketId={ticket.id}
            ticketTitle={ticket.title}
            onClose={() => setShowScoreModal(false)}
            onSuccess={() => {
              setShowScoreModal(false);
              loadTicket(); // Recargar ticket para ver el score actualizado
            }}
          />
        )}
      </div>
    </div>
  );
}